
/* eslint max-lines: off */
import DexDataTable from "@/components/datatable/DexDataTable.vue";
import Column from 'primevue/column';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import InputText from 'primevue/inputtext';
import Dropdown from 'primevue/dropdown';
import { i18n } from "@/main";
import {formatDate as formatDateUtil, getLocaleCode} from "@/util/DateHelper";
import {ListDocument} from "@/apps/squeeze/interfaces/ListDocument";
import {DocumentFilterObject, DocumentSortObject} from "@/apps/squeeze/interfaces/DocumentSearch";
import {FilterMatchMode} from "primevue/api";
import {ExportStatus} from "@dex/squeeze-client-ts/dist/api";
import Tooltip from "primevue/tooltip";
import {PaginationDto, QueueEntry} from "@dex/squeeze-client-ts";
import {useSqueezeStore} from "@/apps/squeeze/store";
import { MenuItem } from '@/shims-prime-vue';
import {useConfirm} from "primevue/useconfirm";
import {computed, defineComponent, onMounted, PropType, reactive, ref, watch} from "vue";
import {useI18n} from "vue-i18n";
import { getSqueezeStepIconMap } from "@/apps/squeeze/App.vue";
import {ToastManager} from "@/util/ToastManager";
import {useToast} from "primevue/usetoast";
import {ClientManager} from "@/singletons/ClientManager";
import Menu from "primevue/menu";
import EntryDialog from "@/components/EntryDialog.vue";
import BlockUI from "primevue/blockui";
import QueueDeleteForm from "@/apps/administration/components/queue/QueueDeleteForm.vue";
import HelpFunctionalityList from "@/apps/squeeze/components/HelpFunctionalityList.vue";
import OverlayPanel from "primevue/overlaypanel";
import {FieldColumn} from "@/apps/squeeze/interfaces/FieldColumn";
import {TableSettings} from "@/util/TableSettings";
import {useRoute} from "vue-router";

interface FrozenColumns {
	step: boolean;
	status: boolean;
	errorText: boolean;
}

interface ColumnWidth extends FieldColumn {
	colWidth?: string;
	minWidth?: string;
}


/**
 * @description Uses Elastic Search API endpoint instead of Queue API endpoint
 */
export default defineComponent({
	name: "DocumentList",
	components: {
		OverlayPanel,
		DexDataTable,
		Column,
		IconField,
		InputIcon,
		InputText,
		Dropdown,
		Menu,
		EntryDialog,
		BlockUI,
		QueueDeleteForm,
		HelpFunctionalityList,
	},
	props: {
		documentClassId: {
			type: Number,
			required: true,
		},
		documentClassName: {
			type: String,
			required: true,
		},
		documents: {
			type: Array as PropType<ListDocument[]>,
			default: () => [],
		},
		fieldColumns: {
			type: Array as PropType<ColumnWidth[]>,
			default: () => [],
		},
		loaded: {
			type: Boolean,
			default: false,
		},
		paginationInfo: {
			type: Object as PropType<PaginationDto>,
		},
		filterOptions: {
			type: Object as PropType<DocumentFilterObject>,
		},
		fullText: {
			type: String,
			default: '',
		},
		showSelectSearch: {
			type: Boolean,
			default: false,
		},
	},
	directives: {
		'tooltip': Tooltip,
	},
	emits: ["onRowSelect", "onReload", "onPage", "onFilter", "onSort", "update:fullText", "openSaveSearch", "openSelectSearch", "clearSearch"],
	setup(props, { emit }) {
		const {t} = useI18n();
		const route = useRoute();
		const store = useSqueezeStore();
		const toast = useToast();
		const confirm = useConfirm();

		const documentList = ref<any>();

		/** Filters of list */
		const filters = ref<DocumentFilterObject>(props.filterOptions!);

		/** QueueStates for filter selection */
		const statuses =  ref<string[]>([
			'INITIAL', 'WORK', 'WAITING', 'ERROR', 'SUSPEND', 'CLARIFICATION', 'FINISHED',
		]);

		/** QueueStates for filter selection */
		const steps =  ref<string[]>([
			'Backup', 'Delete', 'SplitDocument', 'Validation',
		]);

		/** List of allowed filters */
		const allowedFilters = computed(() => {
			return [{value: FilterMatchMode.EQUALS, label: t('Squeeze.Queue.Filter.Options.EQUALS')},
				{value: FilterMatchMode.CONTAINS, label: t('Squeeze.Queue.Filter.Options.CONTAINS')},
				{value: FilterMatchMode.STARTS_WITH, label: t('Squeeze.Queue.Filter.Options.STARTS_WITH')},
				{value: FilterMatchMode.ENDS_WITH, label: t('Squeeze.Queue.Filter.Options.ENDS_WITH')}]
		})

		/** Currently-Selected row */
		const selection = ref([]);

		/** Current locale */
		/*const lang = ref(DEFAULT_LOCALE);*/

		/** Requeue steps to choose in menu */
		const queueEntrySteps = ref<MenuItem[]>([]);

		/** Document API endpoint */
		const documentApi = ClientManager.getInstance().squeeze.document;

		/** User API endpoint */
		const userApi = ClientManager.getInstance().squeeze.user;

		/** Should the table be locked? */
		const lockTable = ref<boolean>(false);

		/** Ref for menu */
		const multiEntryMenu = ref(null);

		/** Is Delete Document Dialog visible? */
		const showDelete = ref<boolean>(false);

		/** Comment for delete of document */
		const comment = ref<string>('');

		/** Show error validation-messages in form? */
		const showValidationMessage = ref(false);

		/** Is the Form of the migrations invalid? */
		const isValidationInvalid = ref(true);

		/** Text (delete) of the header in Entry-Dialog */
		const deleteHeaderText = computed(() => {
			if (selection.value.length > 1) {
				return t('Squeeze.Queue.General.DeleteDocuments', { count: selection.value.length});
			}
			return t('Squeeze.Queue.General.DeleteDocument');
		});

		/** Is the Documentation Dialog visible? */
		const showDocumentationDialog = ref<boolean>(false);

		/** Current filter field name*/
		const currentFilterFieldName = ref<string>('');

		/** Current filter field type */
		const currentFilterFieldType = ref<string>('');

		/** Component of the overlay panel for the documentation of all functionalities */
		const overlayFunctionalityList = ref<any>();

		/** Which columns are frozen? */
		const frozenColumns = reactive<FrozenColumns>({
			step: true,
			status: true,
			errorText: false,
		});

		/** List of all table column widths */
		const allColumnWidths = ref<any[]>([]);

		/** Set frozen columns */
		const setFrozenColumns = (column: string) => {
			(frozenColumns as any)[column] = !(frozenColumns as any)[column];
		}

		/**
		 * Toggle the overlay panel
		 * Is the overlay panel shown?
		 * @param event
		 */
		const toggleOverlay = (event: any) => {
			overlayFunctionalityList.value.toggle(event);
		}

		/**
		 * Triggered if the filter field help icon is clicked
		 * @param {string} columnName
		 * @param {string} columnType
		 */
		const onClickFilterFieldHelpIcon = (columnName: string, columnType: string) => {
			currentFilterFieldName.value = columnName;
			currentFilterFieldType.value = columnType;
			showDocumentationDialog.value = true;
		}

		/** Triggered on update of attribute-form */
		const onUpdate = (data: { comment: string }, isInvalid: boolean) => {
			isValidationInvalid.value = isInvalid;
			comment.value = data.comment;
		}

		const onFilter = (tableFilters: DocumentFilterObject) => {
			emit("onFilter", tableFilters);
		}

		/**
		 * Triggered when a row is clicked
		 * @param event
		 */
		const onRowClick = (event: { originalEvent: MouseEvent; data: QueueEntry; index: number }) => {
			/* Prevent the row-click in the selection-column with the checkbox
			* Use the selectionMode 'multiple' only at the respective column and not at the table, when you also use the row-click event
			* If the selectionMode is set on the table and the row click event is active, these two events overwrite each other
			*/
			if (event.originalEvent
				&& !(event.originalEvent.target as HTMLElement).matches('.p-selection-column')
				&& !(event.originalEvent.target as HTMLElement).matches('.p-checkbox *')) {
				emit("onRowSelect", event.data);
			}
			return;
		}

		/** Triggered when the next page is selected */
		const onPage = (event: any) => {
			emit("onPage", event)
		}

		/** Triggered on sort a column */
		const onSort = (event: { multiSortMeta: DocumentSortObject[] }) => {
			emit("onSort", event.multiSortMeta)
		}

		/** Triggered when table content should be reloaded */
		const onReload = () => {
			emit("onReload");
		}

		/** Opens the dialog for saving/deleting searches */
		const showSaveSearch = () => {
			documentList.value.onFilterApply();
			emit("openSaveSearch");
		}

		/** Opens the Search Selection */
		const openSelectSearch = () => {
			emit("openSelectSearch");
		}

		/** Clears the current search */
		const clearSearch = () => {
			emit("clearSearch");
		}

		/** Formats date string to desired locale string
		 * @param dateToFormat
		 */
		const formatDate = (dateToFormat: string): string => formatDateUtil(dateToFormat, i18n.global.locale);

		/** Formats an amount to locale string (Formats are only defined in the backend) */
		const formatAmounts = (value: string) => {
			value = value.replace(/[^0-9.,-]/g, "");

			if (value.indexOf(",") != -1) {
				value = value.replaceAll(".", "").replace(",", ".");
			}

			if (value.length > 0) {
				const localeCode = getLocaleCode(i18n.global.locale);
				return parseFloat(value).toLocaleString(localeCode, {minimumFractionDigits: 2});
			} else {
				return "0,00";
			}
		}

		/** Formats given values to data type specific formats */
		const formatValue = (value: string, dataType: string) => {
			if (!value) {
				return "";
			}

			switch(dataType.toLowerCase()) {
			case "amount": return formatAmounts(value);
			//case "date": return formatDate(value);
			default: return value;
			}
		}

		/**
		 * Creates the Tooltip for Exports
		 * @param exports
		 */
		const getExportsForToolTip = (exports: ExportStatus[]) => {
			let toolTip = "";
			exports.forEach(exportData => {
				toolTip += exportData.interfaceName + ":\n " + t("Squeeze.Queue.States." + exportData.status) + "\n " + formatDate(exportData.timeStamp!) + "\n" + exportData.result + "\n\n";
			})

			return toolTip;
		}

		/**
		 * Opens multi requeue menu
		 * @param event
		 */
		const openMultiRequeueMenu = (event: any) => {
			if (!selection.value.length) {
				return;
			}
			const multiMenu = multiEntryMenu.value as any;
			if(multiMenu) {
				multiMenu.toggle(event);
			}
		}

		/**
		 * Requeue the selected entries
		 * @param step
		 * @param comment
		 */
		const requeueSelectedEntries = (step: string, comment?: string) => {
			if (!selection.value || !selection.value.length) {
				return;
			}

			// delete dialog
			if (isValidationInvalid.value && step === 'Delete') {
				showValidationMessage.value = true;
				return;
			}

			lockTable.value = true;
			selection.value.reduce((chain, { id }) => {
				if (id == undefined) {
					return chain;
				}
				return chain.finally(() => documentApi.requeueDocument(id, step, undefined, comment ?? "").catch(() => { /* swallow errors */ }))
			}, Promise.resolve()).finally(() => {
				ToastManager.showSuccess(toast, t('Squeeze.Queue.Requeue.Success'), t('Squeeze.Queue.Steps.' + step));
				selection.value = [];
				lockTable.value = false;
				showDelete.value = false;
				onReload();
			});
		}

		/**
		 * Get base column width
		 * @param fieldName
		 * @param width
		 */
		const getBaseColumnWidth = (fieldName: string, width: number) => {
			const colWidth = allColumnWidths.value
				.find(column => column.name === fieldName)
				?.colWidth ?? width;
			return 'min-width: ' + colWidth + 'rem;';
		}

		/** Reset column widths */
		const resetColumnWidths = () => {
			// reset array
			allColumnWidths.value = [];

			// delete column widths
			props.fieldColumns.forEach(field => {
				if (field.colWidth) {
					delete field.colWidth;
				}
			});

			TableSettings.saveDocumentListTableColumnWidths(t, toast, store, userApi, props.documentClassId, allColumnWidths.value);
		}

		/**
		 * Get base column config
		 * @param routeName
		 */
		const getBaseColumnConfig = (routeName: string) => {
			// check route
			const isValidationView = routeName === 'DocumentListValidation' || routeName === 'DocumentListValidationWithSearch';

			// base columns of datatable
			const baseColumns = [
				{ index: 0, field: 'selection' },
				{ index: 1, field: 'id' },
				{ index: 2, field: isValidationView ? 'status' : 'step' },
				{ index: 3, field: isValidationView ? 'create_ts' : 'status' },
				{ index: 4, field: isValidationView ? 'errorText' : 'create_ts' },
			];

			// column that is only added in the non-validation view
			// in this particular case, it is also a base column.
			const optionalColumn = !isValidationView
				? [{ index: 5, field: 'errorText' }]
				: [];

			return {
				validationView: isValidationView,
				columns: [
					...baseColumns,
					...optionalColumn,
				],
			};
		};

		/** Trigger the column resize
		 * @param event
		 */
		const columnResize = (event: any) => {
			// convert px to rem
			const columnWidthInRem = (1 / 16 * parseFloat(event.element.offsetWidth));

			// get current column (of dynamics field columns)
			const currentColumn = props.fieldColumns.find((column: ColumnWidth) => column.header === event.element.innerText) as ColumnWidth;
			if (currentColumn && currentColumn.id && columnWidthInRem) {
				if (allColumnWidths.value && allColumnWidths.value.find((columnWidth: any) => columnWidth.id === currentColumn.id)) {
					// if a column width has already been set, it will be overwritten
					const col = allColumnWidths.value.find((columnWidth: any) => columnWidth.id === currentColumn.id);
					col.colWidth = String(columnWidthInRem);
				} else {
					// if no column width exists, it will be set
					currentColumn.colWidth = String(columnWidthInRem);
					allColumnWidths.value = [...allColumnWidths.value, currentColumn];
				}
			} else {
				// get base column
				const columnElement = event.element?.closest('th[role="columnheader"]');
				const columnIndex = columnElement?.cellIndex;

				if (columnIndex === null || columnIndex === undefined) return;

				const config = getBaseColumnConfig(route.name as string);
				const columnConfig = config.columns.find(col => col.index === columnIndex);

				if (!columnConfig) return;

				// check if column exists in allColumnWidths and set the new column width
				if (allColumnWidths.value && allColumnWidths.value.find((columnWidth: any) => columnWidth.name === columnConfig.field)) {
					const col = allColumnWidths.value.find((columnWidth: any) => columnWidth.name === columnConfig.field);
					col.colWidth = String(columnWidthInRem);
				} else {
					// set column width of a base column when not exists
					const currentColumn = {
						colWidth: String(columnWidthInRem),
						header: event.element.innerText,
						id: null,
						name: columnConfig.field,
						type: "Text",
					};

					allColumnWidths.value = [...allColumnWidths.value, currentColumn];
				}
			}

			TableSettings.saveDocumentListTableColumnWidths(t, toast, store, userApi, props.documentClassId, allColumnWidths.value);
		};

		watch(props.filterOptions!, () => {
			Object.assign(filters.value, props.filterOptions);
		})

		onMounted(() => {
			// Initialize all column widths
			if (store.state.userSettings.tableSettings.length
				&& 'documentList' in JSON.parse(store.state.userSettings.tableSettings)
				&& JSON.parse(store.state.userSettings.tableSettings).documentList[props.documentClassId]) {
				allColumnWidths.value = JSON.parse(store.state.userSettings.tableSettings).documentList[props.documentClassId];
			}

			queueEntrySteps.value = [{
				label: t('Squeeze.Queue.General.Steps'),
				items: [],
			}];
			getSqueezeStepIconMap().forEach((icon: string, step: string) => {
				if (step === 'Delete') {
					queueEntrySteps.value[0].items.push({
						label: t('Squeeze.Queue.Steps.' + step),
						icon: icon,
						command: () => {
							showDelete.value = true;
						},
					})
				} else {
					queueEntrySteps.value[0].items.push({
						label: t('Squeeze.Queue.Steps.' + step),
						icon: icon,
						command: (ev: { item: any; originalEvent: MouseEvent }) => {
							let messageText = '';
							let acceptFn = null;

							if ((ev.originalEvent.target as HTMLElement).matches('#multi_entry_menu *')) {
								// If entry in multi_menu is clicked (Requeue-Button under table for multi-selection)
								messageText = t('Squeeze.Queue.Requeue.ForwardEntries');
								acceptFn = () => {
									requeueSelectedEntries(step);
								};
							}
							else {
								return;
							}

							confirm.require({
								message: messageText + ": " + t('Squeeze.Queue.Steps.' + step) + " ?",
								header: t('Squeeze.General.Confirmation'),
								icon: 'pi pi-exclamation-triangle',
								accept: acceptFn,
								reject: () => { /**/ },
							});
						},
					});
				}
			});
		})

		return {
			filters, allowedFilters, steps, statuses, store, queueEntrySteps, selection, multiEntryMenu,
			lockTable, showDelete, comment, showValidationMessage, isValidationInvalid, deleteHeaderText,
			showDocumentationDialog, currentFilterFieldName, currentFilterFieldType, overlayFunctionalityList,
			frozenColumns, allColumnWidths, setFrozenColumns, toggleOverlay, onClickFilterFieldHelpIcon,
			onReload, onSort, onPage, onRowClick, onFilter, formatDate, onUpdate, requeueSelectedEntries, getBaseColumnWidth,
			resetColumnWidths, openMultiRequeueMenu, getExportsForToolTip, formatValue, showSaveSearch, openSelectSearch,
			clearSearch, documentList, columnResize,
		}
	},
});
